import type { PersonNameParts } from '../entities/Person';

/**
 * La parte local del correo corporativo, derivada del nombre.
 *
 * El patrón de la casa es `primernombre.primerapellido`. Cuando ya está tomado
 * hace falta una alternativa, y **la alternativa tiene que ser predecible**: un
 * correo es algo que la gente teclea de memoria y le dicta a otros por teléfono.
 * `jose.lugardo2` es peor que `jose.ramirez` aunque las dos estén libres, y por
 * eso el sufijo numérico es el último recurso y no el primero.
 *
 * Este archivo sólo **propone**. No sabe qué correos existen —eso es una consulta,
 * no una regla— y nunca decide: quien captura puede escribir el que quiera. Lo
 * que evita es que la elección arranque de una caja vacía, que es donde nacen
 * `jc.lugardo`, `mrivas` y `lfernandez` conviviendo en el mismo dominio.
 */

/**
 * Cómo se dobla una parte del nombre para que quepa en un correo.
 *
 * Tres cosas a la vez, y las tres importan:
 *
 * - **Los acentos se descomponen y se tiran**, no se sustituyen a mano. `NFD`
 *   separa «í» en «i» + tilde combinante y la clase `\p{M}` se lleva la tilde;
 *   la «ñ» sale «n» por el mismo camino. Una tabla de reemplazos escrita a mano
 *   se habría olvidado de la diéresis de «Argüelles».
 * - **Los espacios desaparecen sin dejar separador.** «de la Cruz» es un apellido
 *   y su correo es `delacruz`, no `de.la.cruz` — el punto separa nombre de
 *   apellido y usarlo también dentro de uno destruye esa lectura.
 * - **Lo que no es letra ni dígito se cae**: apóstrofos de «D'Angelo», guiones de
 *   «Ana-María», el punto de «Ma. Guadalupe».
 */
export const foldForEmail = (part: string): string =>
  part
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/** Sólo la inicial, para los patrones que la usan. */
const initial = (part: string): string => foldForEmail(part).slice(0, 1);

/**
 * Los candidatos, del más deseable al menos, ya normalizados y sin repetidos.
 *
 * El orden es el que pidió el negocio y no una permutación exhaustiva: primero
 * el patrón de la casa, después cambiar **una** de las dos mitades —que es lo que
 * un humano probaría—, y sólo al final las formas que ya no se parecen al patrón.
 *
 * Devuelve al menos uno siempre, porque primer nombre y primer apellido son
 * obligatorios en el alta. Nunca devuelve un sufijo numérico: para eso está
 * `numberedFrom`, que existe aparte precisamente para que quede claro que es
 * otra cosa.
 */
export const localPartCandidates = (parts: PersonNameParts): readonly string[] => {
  const n1 = foldForEmail(parts.primerNombre);
  const n2 = foldForEmail(parts.segundoNombre ?? '');
  const a1 = foldForEmail(parts.primerApellido);
  const a2 = foldForEmail(parts.segundoApellido ?? '');

  const candidates = [
    // El patrón de la casa.
    `${n1}.${a1}`,
    // Cambia el apellido, conserva el nombre. Es el que menos desorienta.
    a2.length > 0 ? `${n1}.${a2}` : '',
    // Cambia el nombre, conserva el apellido.
    n2.length > 0 ? `${n2}.${a1}` : '',
    n2.length > 0 && a2.length > 0 ? `${n2}.${a2}` : '',
    // Los dos apellidos juntos: sigue siendo legible y ya no choca con nadie que
    // comparta sólo uno.
    a2.length > 0 ? `${n1}.${a1}${a2}` : '',
    // Inicial más apellido — la forma que ya existe en el padrón (`mrivas`).
    `${initial(parts.primerNombre)}${a1}`,
    n2.length > 0 ? `${initial(parts.primerNombre)}${initial(parts.segundoNombre ?? '')}.${a1}` : '',
  ].filter((candidate) => candidate.length > 0 && !candidate.startsWith('.') && !candidate.endsWith('.'));

  return [...new Set(candidates)];
};

/**
 * El último recurso: el patrón de la casa con un número.
 *
 * Existe porque los candidatos son finitos y dos personas pueden llamarse igual
 * de verdad. Se aparta de los demás a propósito — si esto se ofrece, es que ya
 * se agotó todo lo que se puede dictar por teléfono sin deletrear.
 */
export const numberedFrom = (base: string, n: number): string => `${base}${String(n)}`;
